import { radioGroupArrowKeyNavigation, triggerNextQuizPopup } from '../helpers';

let shouldUpdateTotalParticipants = true;

/**
 * Checks if all questions have been answered and updates the completed participants count.
 * @param {string} pollQuizId - The poll quiz unique id
 * @param {array} questionCollection - All poll or survey elements.
 *
 * @returns {void}
 */
const checkIfAllQuestionsAnswered = (pollQuizId, questionCollection) => {
	// Set a timeout to check if all questions have been answered.
	const allVoted = Array.from(questionCollection).every((question) =>
		question.nextElementSibling.classList.contains('has-voted'),
	);

	// If all questions have been answered, update the completed participants count.
	if (allVoted) {
		wp.apiFetch({
			path: `/mavis/v1/poll-quiz-completed-participants`,
			method: 'POST',
			data: {
				poll_quiz_id: pollQuizId,
			},
		})
			.catch((err) => console.error(err.message)) // eslint-disable-line
			.then((data) => JSON.parse(data));

		// Find the main wrapper based on the first question
		if (questionCollection.length > 0) {
			const wrapper = questionCollection[0].closest('.wp-block-mavis-poll-quiz');
			if (wrapper) {
				triggerNextQuizPopup(wrapper, '.mavisPollQuizResults');
			}
		}
	}
};

/**
 * Jumps to the factoid of the last answered question.
 *
 * @param {HTMLElement} relevantQuestion - The question that has been answered
 *
 * @returns {void}
 *
 */
const jumpToFactoid = (resultsContainer, factoid) => {
	const nextQuestion = resultsContainer.nextElementSibling;

	if (factoid && factoid.classList.contains('mavisPollQuiz-factoid')) {
		setTimeout(() => {
			factoid.scrollIntoView({ behavior: 'smooth' });
		}, 1000);
	} else if (nextQuestion) {
		setTimeout(() => {
			nextQuestion.scrollIntoView({ behavior: 'smooth' });
		}, 1000);
	}
};

/**
 * Posts to the poll-votes custom endpoint.
 *
 * @param {string} votedOptionId - The poll option unique id.
 * @param {string} questionId - The poll unique id
 * @param {string} pollQuizId - The poll quiz unique id
 */
const addVotes = async (votedOptionId, questionId, pollQuizId) =>
	wp
		.apiFetch({
			path: `/mavis/v1/poll-quiz-question-votes`,
			method: 'POST',
			data: {
				question_id: questionId,
				option_id: votedOptionId,
				poll_quiz_id: pollQuizId,
				update_total_participants: shouldUpdateTotalParticipants,
			},
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Updates the percentages based on vote.
 *
 * @param {string} votedOptionId - The poll option unique id.
 * @param {string} questionId - The poll unique id
 */
const updateVotes = async (votedOptionId, questionId, pollQuizId, showResults = true) => {
	// Adds votes to post_meta.
	const votes = await addVotes(votedOptionId, questionId, pollQuizId);

	// Get the poll quiz results container and add a has-voted class.
	const answeredQuestionResults = document.querySelector(`
    .mavisPollQuizResults[data-question-id="${questionId}"]
    `);
	answeredQuestionResults.classList.add('has-voted');

	// Add `is-selected` class to the chosen option.
	const selectedPollContainer = document.querySelector(`
    .mavisPollQuizResults[data-question-id="${questionId}"] .mavisPollQuizResult[data-option-id="${votedOptionId}"]
  `);
	selectedPollContainer.classList.add('is-selected');

	// Set variable to store the options for the answered question.
	let answeredQuestionOptions = '';

	// Loop through the votes to find the answered question.
	votes.forEach((option) => {
		if (option.id === questionId) {
			answeredQuestionOptions = option.answers;
		}
	});

	// Update vote percentages for the answered question.
	answeredQuestionOptions.forEach((option) => {
		const resultNum = document.querySelector(`
      .mavisPollQuizResults[data-question-id="${questionId}"] .mavisPollQuizResult[data-option-id="${option.id}"] .mavisPollQuizResult-percentage
    `);

		const resultBar = document.querySelector(`
      .mavisPollQuizResults[data-question-id="${questionId}"] .mavisPollQuizResult[data-option-id="${option.id}"] progress
    `);

		resultBar.value = option.vote_percentage;
		resultNum.innerHTML = `${option.vote_percentage}%`;
	});

	const results = document.querySelector(`.mavisPollQuizResults[data-question-id="${questionId}"]`);

	// Reveal results if showResults is true.
	if (showResults) {
		// Remove the form.
		const questionToRemove = document.querySelector(`
      .wp-block-mavis-poll-quiz-question[data-question-id="${questionId}"] .wp-block-poll-quiz-options-container
      `);
		questionToRemove.remove();

		results.classList.add('has-voted', 'is-active');
	}

	shouldUpdateTotalParticipants = false;

	checkIfAllQuestionsAnswered(
		pollQuizId,
		document.querySelectorAll('.wp-block-mavis-poll-quiz-question'),
	);
};

/**
 * Add event listeners to each poll/survey on the save.
 *
 * @param {array} questionCollection All poll or survey elements.
 */
const addOptionClickedListeners = (questionCollection, pollQuizId, isReduced) => {
	questionCollection.forEach((question) => {
		// eslint-disable-next-line complexity
		question.addEventListener('click', (event) => {
			const triggers = ['INPUT'];

			// Ignore if clicked outside of input/label.
			if (!triggers.includes(event.target.tagName)) {
				return;
			}

			if (triggers.includes(event.target.tagName)) {
				event.preventDefault();
			}

			const { questionId } = event.target.closest(`.wp-block-mavis-poll-quiz-question`).dataset;

			const optionParent = event.target.parentElement;

			if (optionParent.classList.contains(`wp-block-mavis-poll-quiz-options`)) {
				optionParent.classList.add('is-selected');
			}

			const votedOptionId = event.target.value;

			// Update the vote post_meta, vote percetages, reorder votes and remove options.
			updateVotes(votedOptionId, questionId, pollQuizId, true);

			// Get the factoid element
			const resultsContainer = document.querySelector(`
        .mavisPollQuizResults[data-question-id="${questionId}"]
      `);

			// Get the factoid element
			const factoidEl = resultsContainer.querySelector('.mavisPollQuiz-factoid');

			// When an option has been selected, add a class to reveal the factoid
			if (factoidEl) {
				factoidEl.classList.add('is-active');
			}

			if (!isReduced) {
				jumpToFactoid(resultsContainer, factoidEl);
			}
		});
	});
};

window.addEventListener('DOMContentLoaded', () => {
	// Check if the user has reduced motion enabled
	const isReduced =
		window.matchMedia(`(prefers-reduced-motion: reduce)`) === true ||
		window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

	let pollQuizId = null;

	// Get any existing data
	const allPollQuizQuestions = [...document.querySelectorAll('.wp-block-mavis-poll-quiz-question')];
	const pollQuizElement = document.querySelector('.wp-block-mavis-poll-quiz');

	if (pollQuizElement) {
		pollQuizId = pollQuizElement.dataset.questionId;
	}

	if (allPollQuizQuestions.length > 0) {
		addOptionClickedListeners(allPollQuizQuestions, pollQuizId, isReduced);

		allPollQuizQuestions.forEach((question) => {
			const inputGroups = question.querySelectorAll('fieldset');
			radioGroupArrowKeyNavigation(inputGroups);
		});
	}
});
